import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import Table from "components/Table";
import useConsent from "hooks/useConsent";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import configData from "config/config.json";
import sessionHelper from "services/sessionHelper";
import {
  deleteChallengeRewardImage,
  getChallengeRewardImages,
} from "services/challengeRewardImageService";
import { PermittedRoles } from "utils/permissions";
import { REWARD_TYPE_LABELS } from "./rewardTypeConfig";

const useStyles = makeStyles(() => ({
  container: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
  },
  preview: {
    width: 72,
    height: 72,
    objectFit: "contain",
    background:
      "repeating-conic-gradient(#eee 0% 25%, #fafafa 0% 50%) 50% / 12px 12px",
    border: "1px solid #e0e0e0",
    borderRadius: 6,
    padding: 3,
  },
  previewEmpty: {
    width: 72,
    height: 72,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px dashed #cfcfcf",
    borderRadius: 6,
    color: "#bdbdbd",
    margin: "0 auto",
  },
}));

// Thumbnail that degrades to a placeholder instead of a broken-image icon.
const ImageThumb = ({ url, label }: { url?: string; label: string }) => {
  const styles = useStyles();
  const [errored, setErrored] = useState(false);
  const busted = url ? `${url.split("?")[0]}?t=${Date.now()}` : url;

  return url && !errored ? (
    <img
      src={busted}
      alt={label}
      className={styles.preview}
      onError={() => setErrored(true)}
    />
  ) : (
    <Box className={styles.previewEmpty}>
      <BrokenImageOutlinedIcon fontSize="small" />
    </Box>
  );
};

interface RowActionsProps {
  data: any;
  actionHandlers: {
    handleDelete?: (data: any) => void;
  };
}

const RowActions = ({
  data,
  actionHandlers: { handleDelete = () => {} },
}: RowActionsProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.CHALLENGE_REWARD_IMAGES.DELETE_ROLES,
  );

  const handleClose = (event?: any) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleEditClick = (event: any, rowData: any) => {
    handleClose(event);
    navigate(
      configData.routes.challenges.editRewardImages?.replace(
        ":id",
        rowData?._id,
      ),
    );
  };

  const handleDeleteClick = (event: any, rowData: any) => {
    handleClose(event);
    handleDelete(rowData);
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={(e) => handleEditClick(e, data)}>Edit</MenuItem>
        {hasDeletePermission && (
          <MenuItem onClick={(e) => handleDeleteClick(e, data)}>
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const columns = [
  {
    id: "preview",
    label: "Image",
    isSortable: false,
    alignHead: "center",
    alignBody: "center",
    renderCell: (data: any) => (
      <ImageThumb url={data.imageUrl} label={data.rewardType} />
    ),
  },
  {
    id: "rewardTypeLabel",
    label: "Reward Type",
    type: "string",
    isSortable: true,
    alignHead: "center",
    alignBody: "center",
  },
  {
    id: "descriptionTemplate",
    label: "Description",
    isSortable: false,
    alignHead: "center",
    alignBody: "left",
    renderCell: (data: any) => (
      <Typography variant="body2" sx={{ maxWidth: 360 }}>
        {data.descriptionTemplate || "—"}
      </Typography>
    ),
  },
  {
    id: "translationCount",
    label: "Translations",
    isSortable: true,
    alignHead: "center",
    alignBody: "center",
  },
  { id: "actions", label: "Actions" },
];

const ChallengeRewardImages = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [listData, setListData] = useState<any[]>([]);

  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.CHALLENGE_REWARD_IMAGES.UPDATE_ROLES,
  );

  const navigateToCreate = () =>
    navigate(configData.routes.challenges.createRewardImages);

  const rowHref = (data: any) =>
    hasEditPermission
      ? configData.routes.challenges.editRewardImages?.replace(":id", data?._id)
      : null;

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await getChallengeRewardImages();
      const items = resp?.data || [];
      setListData(
        items.map((item: any) => ({
          ...item,
          rewardTypeLabel: REWARD_TYPE_LABELS[item.rewardType] || item.rewardType,
          translationCount: item.descriptionTemplateTrans
            ? Object.keys(item.descriptionTemplateTrans).length
            : 0,
        })),
      );
    } catch (error: any) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const _openConsentDialog = (data: any) => {
    openConsent();
    updateDetails({
      itemName: REWARD_TYPE_LABELS[data?.rewardType] || data?.rewardType,
      id: data?._id,
    });
  };

  const onDelete = async () => {
    try {
      const resp = await deleteChallengeRewardImage(constentDetails?.id);
      showSnackbar(resp?.data?.message);
      fetchData();
      closeConsent();
    } catch (error: any) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

  const filteredColumns = columns.filter(
    (col) => col.id !== "actions" || hasEditPermission,
  );

  return (
    <ContentShell
      title="Challenge Reward Images"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          {sessionHelper.hasRole(
            PermittedRoles.CHALLENGE_REWARD_IMAGES.CREATE_ROLES,
          ) && <CustomButton onClick={navigateToCreate}>Create</CustomButton>}
        </Box>
      }
    >
      <Box className={styles.container}>
        <Table
          columns={filteredColumns}
          data={listData}
          hidePagination={true}
          highlightAltRow={true}
          actions={RowActions}
          rowHref={rowHref}
          actionHandlers={{
            handleDelete: _openConsentDialog,
          }}
        />
        <DeleteConsentDialog
          itemName={constentDetails?.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={onDelete}
        />
      </Box>
    </ContentShell>
  );
};

export default ChallengeRewardImages;
